import {
  useState,
  useRef,
  useCallback,
  useImperativeHandle,
  useEffect,
  useMemo,
} from "react";

import { useFormContext } from "../contexts/form-context";
import { useTranslate } from "../../translation/hooks/use-translation";
import getValidator from "../services/validate-input";

// key builders
const normalize = (str) => (str || "").trim().toLowerCase();
const countryKey = (c) => c.name || normalize(c.name);
const destKey = (c, d) => `${countryKey(c)}.${d.name || normalize(d.name)}`;
const allKey = (c) => `${countryKey(c)}.all`;

const useDestinationSelector = (props) => {
  const { name } = props;
  const ref = useRef();

  const { t } = useTranslate();
  const validateInput = getValidator(t);
  const { register, deregister, updateValidity } = useFormContext();

  // ---------- internal state ----------
  const [values, setValues] = useState(props.defaultValue || []);
  const [disabled, setDisabled] = useState(props.disabled || false);
  const [error, setError] = useState(null);
  const [displayError, setDisplayError] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  // accordion state
  const [expanded, setExpanded] = useState("");

  // derived splits
  const categories = useMemo(
    () => values.filter((v) => ["beach", "city", "ski", "exotic"].includes(v)),
    [values]
  );

  const destinations = useMemo(
    () => values.filter((v) => !["beach", "city", "ski", "exotic"].includes(v)),
    [values]
  );

  // ---------- validation ----------
  const validate = useCallback(
    (list = values) => {
      const err = validateInput(props, list);
      setError(err);
      updateValidity(name, err);
      return err;
    },
    [values, name]
  );

  // validate whenever values change
  useEffect(() => {
    validate(values);
    if (ref.current) ref.current.value = values;
  }, [values]);

  // error display sync (identical to useCheckbox)
  useEffect(() => {
    setDisplayError(hasInteracted ? !!error : false);
  }, [error, hasInteracted]);

  // ---------- value manipulation identical to useCheckbox ----------
  const add = useCallback(
    (val) => {
      const list = Array.isArray(val) ? val : [val];
      const newList = [...new Set([...values, ...list])];
      setValues(newList);
      setHasInteracted(true);
      ref.current.value = newList;
    },
    [values]
  );

  const remove = useCallback(
    (val) => {
      const toRemove = new Set(Array.isArray(val) ? val : [val]);
      const newList = values.filter((v) => !toRemove.has(v));
      setValues(newList);
      setHasInteracted(true);
      ref.current.value = newList;
    },
    [values]
  );

  const update = useCallback((val) => {
    setValues(val);
    ref.current.value = val;
    setHasInteracted(true);
  }, []);

  const reset = useCallback(() => {
    const resetValues = props.defaultValue || [];
    setValues(resetValues);
    ref.current.value = resetValues;
    setHasInteracted(false);
    setDisplayError(false);
    setError(null);
  }, [props.defaultValue]);

  const onChange = useCallback(
    (e) => {
      const { value, checked } = e.target;
      checked ? add(value) : remove(value);
    },
    [add, remove]
  );

  const onBlur = useCallback(() => {
    if (!hasInteracted) setHasInteracted(true);
    if (error) setDisplayError(true);
  }, [hasInteracted, error]);

  // ---------- category & destination toggles ----------
  const toggleCategory = (cat, checked) =>
    checked ? add(cat) : remove(cat);

  const toggleDestination = (country, d, checked) => {
    const key = destKey(country, d);
    checked ? add(key) : remove(key);
  };

  const toggleAllInCountry = (country) => {
    const a = allKey(country);
    const dList = country.destinations.map((d) => destKey(country, d));

    if (values.includes(a)) {
      remove([a, ...dList]);
    } else {
      add([a, ...dList]);
    }
  };

  const disableCategories = destinations.length > 0;
  const disableDestinations = categories.length > 0;

  // ---------- imperative handle (MATCHES useCheckbox EXACTLY) ----------
  useImperativeHandle(
    ref,
    () => ({
      reset,
      validate: () => validate(values),
      setValue: update,
      getId: () => name,
      value: values,
      name,
      id: name,
      getName: () => name,
      isValid: () => !error,
      setDisabled,
      setDisplayError,
    }),
    [values, error]
  );

  // register field
  useEffect(() => {
    register(name, ref);
    return () => deregister(name);
  }, [name]);

  return {
    ref,
    bind: {
      add,
      remove,
      update,
      values,
      reset,
      onChange,
      onBlur,
      disabled,
      error,
      displayError,
    },

    categories,
    destinations,
    expanded,
    setExpanded,
    disableCategories,
    disableDestinations,

    toggleCategory,
    toggleDestination,
    toggleAllInCountry,

    destKey,
    countryKey,
    allKey,
    normalize,
  };
};

export default useDestinationSelector;
